(ns frontend.ux-overhaul.listing.pipeline-entity
  (:require
   [common.import-permissions :as import-permissions]
   [common.localization :as loc]
   [common.opensearch.row :as os-row]
   [common.schema.listing-definition :as listing-definition]
   [common.teams :as teams]
   [frontend.conn :as conn]
   [frontend.logging :as logging]
   [frontend.page-components.navbar-components.create-dropdown :as create-dropdown]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.ux-overhaul.listing.generic.event :as generic-event]
   [frontend.ux-overhaul.listing.generic.facets-event :as generic-facets-event]
   [frontend.ux-overhaul.listing.generic.model :as generic-model]
   [frontend.ux-overhaul.listing.generic.selection :as selection-helpers]
   [frontend.ux-overhaul.listing.generic.sub :as generic-sub]
   [frontend.ux-overhaul.listing.generic.view :as listing]
   [frontend.ux-overhaul.listing.pipeline-entity.card-actions :as card-actions]
   [frontend.ux-overhaul.listing.pipeline-entity.card-style :as card-style]
   [frontend.ux-overhaul.listing.pipeline-entity.default-views :as default-views]
   [frontend.ux-overhaul.listing.sales-opportunity.won-selector :as sales-won-selector]
   [frontend.utils :as fu]
   [frontend.view.entity :as entity]
   [frontend.view.grid.common.components :as grid-components]
   [frontend.view.grid.common.utils :as grid-utils]
   [frontend.view.modals.bulk-edit-entity.view :as bulk-edit-modal]
   [frontend.view.modals.confirmation.view :as confirmation]
   [frontend.view.modals.curtains.pipeline-settings-curtain.view :as pipeline-settings-curtain]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.folder-management-check-lists-modal.view :as folder-management-check-lists-modal]
   [frontend.view.modals.folder-management-modal.view :as folder-management-modal]
   [frontend.view.not-found :as not-found]
   [fx.ajax :as ajax]
   [promesa.core :as p]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [common.completion :as completion]
   [ui.completion.event :as completion-event]
   [ui.editors.entity-creator.view :as creator-editor]
   [ui.fx.toast :as toast]
   [ui.view.grid-opensearch.event :as grid-event]
   [ui.editors.simple-filters.model :as simple-filters-model]
   [ui.view.grid-opensearch.model :as grid-model]
   [ui.view.kanban-opensearch.event :as kanban-event]
   [ui.view.kanban-opensearch.model :as kanban-model]))

(defn- open-entity!
  [entity-type value]
  (when-let [entity-id (os-row/entity-id value)]
    (routing/open-entity {:db/id entity-id
                          :entity/type entity-type})))

(defn- runtime-listing-id
  [listing-id pipeline-id]
  (str listing-id "-" (or pipeline-id "no-pipeline")))

(defn- selected-pipeline-entity
  [pipelines pipeline-id]
  (some (fn [pipeline]
          (when (= (:db/id pipeline) pipeline-id)
            pipeline))
        pipelines))

(defn- pipeline-title
  [fallback-title pipeline]
  (or (:pipeline/name pipeline)
      (:common/display-name pipeline)
      (loc/tr fallback-title)))

(defn switchable-pipelines
  "The pipelines that the user can switch to: the pipelines that the user can
   access, without the disabled and the inactive ones. `has-access?` tells if the
   user can access a pipeline. The order of the session stays."
  [{:keys [pipelines has-access?]}]
  (->> (or pipelines [])
       (filter has-access?)
       (remove :common/disabled?)
       (remove :pipeline/inactive?)
       vec))

(defn pipeline-menu-items
  "The items of the menu in the title of a pipeline listing, one for each
   pipeline that the user can switch to. There is no menu, and so no items, for a
   user with one pipeline only. `on-select` gets the id of the chosen pipeline."
  [{:keys [pipelines selected-pipeline-id on-select]}]
  (when (< 1 (count pipelines))
    (mapv (fn [{:keys [db/id] :as pipeline}]
            {:key       (str "pipeline-menu-item-" id)
             :label     (pipeline-title :pipeline pipeline)
             :selected? (= id selected-pipeline-id)
             :on-click  (fn [_]
                          (on-select id))})
          pipelines)))

(defn switch-pipeline-fx
  "Show another pipeline of the same type. The effect does what the legacy grids
   did: it sets the module, remembers the pipeline for the user, and opens the
   route of the pipeline."
  [_ [_ {:keys [pipeline-key module route-with-pipeline-id pipeline-id]}]]
  {::apply-pipeline-switch {:pipeline-key           pipeline-key
                            :module                 module
                            :route-with-pipeline-id route-with-pipeline-id
                            :pipeline-id            pipeline-id}})

(rf/reg-event-fx ::switch-pipeline switch-pipeline-fx)

(rf/reg-fx
 ::apply-pipeline-switch
 (fn [{:keys [pipeline-key module route-with-pipeline-id pipeline-id]}]
   (session/set-module! module)
   (swap! session/selected-pipelines assoc pipeline-key pipeline-id)
   (routing/set-view! [route-with-pipeline-id :pipeline-id pipeline-id])))

(defn- title-menu
  "The menu in the title of the listing with the pipelines to switch to, or nil
   when there is no other pipeline."
  [{:keys [all-pipelines pipeline-id pipeline-key module route-with-pipeline-id]}]
  (when-let [items (pipeline-menu-items
                    {:pipelines           (switchable-pipelines
                                           {:pipelines  @all-pipelines
                                            :has-access? session/has-entity-access?})
                     :selected-pipeline-id pipeline-id
                     :on-select           (fn [id]
                                            (rf/dispatch
                                             [::switch-pipeline
                                              {:pipeline-key           pipeline-key
                                               :module                 module
                                               :route-with-pipeline-id route-with-pipeline-id
                                               :pipeline-id            id}]))})]
    {:items      items
     :aria-label (loc/tr :select-pipeline)}))

(def delete-configs
  {:entity.type/oppo
   {:operation :oppo/mass-action
    :action    :delete-deals
    :ids-key   :oppo-ids
    :label     "deals"}

   :entity.type/fundraising-opportunity
   {:operation :fundraising-opportunity/mass-action
    :action    :delete-fundraising-opportunities
    :ids-key   :fundraising-opportunity-ids
    :label     "fundraising-opportunities"}

   :entity.type/sales-opportunity
   {:operation :sales-opportunity/mass-action
    :action    :delete-sales-opportunities
    :ids-key   :sales-opportunity-ids
    :label     "sales-opportunities"}

   :entity.type/exit-opportunity
   {:operation :exit-opportunity/mass-action
    :action    :delete-exit-opportunities
    :ids-key   :exit-opportunity-ids
    :label     "exit-opportunities"}})

(defn- listing-grid-id
  [listing-id]
  (str listing-id "-grid"))

(defn- listing-kanban-id
  [listing-id]
  (str listing-id "-kanban"))

(defn- normalize-field-path
  [field-path]
  (cond
    (vector? field-path) field-path
    (keyword? field-path) [field-path]
    (sequential? field-path) (vec field-path)
    :else nil))

(rf/reg-sub
 ::bulk-edit-open?
 (fn [db [_ listing-id]]
   (boolean (get-in db (conj (generic-model/ui-path listing-id) :bulk-edit-open?)))))

(rf/reg-event-db
 ::open-bulk-edit
 (fn [db [_ {:keys [listing-id]}]]
   (assoc-in db (conj (generic-model/ui-path listing-id) :bulk-edit-open?) true)))

(rf/reg-event-fx
 ::refresh-listing
 (fn [_ [_ {:keys [listing-id]}]]
   ;; Both views of this listing read from OpenSearch, so a write has to
   ;; refresh both. Named rather than repeated as two dispatches per call site,
   ;; so it can also be handed to `ui.completion.event/::await` as a single
   ;; `:on-complete`.
   {:fx [[:dispatch [::grid-event/trigger-query-search (listing-grid-id listing-id)]]
         [:dispatch [::kanban-event/reload-visible-columns
                     {:kanban-id (listing-kanban-id listing-id)}]]]}))

(rf/reg-event-fx
 ::on-index-completion
 (fn [_ [_ {:keys [listing-id]} _payload]]
   ;; Another part of the UI changed an entity, for example a curtain that
   ;; passes a deal, and the backend has now rebuilt its search document. The
   ;; listing has no request of its own to wait for, so it refreshes on the
   ;; completion. The refresh is cheap: the Kanban reloads only the visible
   ;; columns.
   {:fx [[:dispatch [::refresh-listing {:listing-id listing-id}]]]}))

(rf/reg-event-fx
 ::close-bulk-edit
 (fn [{:keys [db]} [_ {:keys [listing-id]}]]
   {:db (assoc-in db (conj (generic-model/ui-path listing-id) :bulk-edit-open?) false)
    :fx [[:dispatch [::refresh-listing {:listing-id listing-id}]]]}))

(rf/reg-event-fx
 ::bulk-delete-selected
 (fn [{:keys [db]} [_ {:keys [listing-id entity-type]}]]
   (let [selection (get-in db (generic-model/selection-path listing-id))
         selected-ids (selection-helpers/action-ids selection)
         {:keys [operation action ids-key label]} (get delete-configs entity-type)]
     (if (and (seq selected-ids) operation action ids-key)
       (do
         (modals/open-modal!
          confirmation/modal-key
          {:body          (selection-helpers/delete-confirmation-body selection)
           :confirm-text  (loc/tr :confirm-delete-selected-button)
           :checkbox-text (loc/tr :confirm-delete-selected-checkbox)
           :on-confirm    (fn []
                            (-> (conn/sendp! operation
                                             {:action action
                                              ids-key selected-ids})
                                (p/then (fn [resp]
                                          (rf/dispatch [::generic-event/clear-selection listing-id])
                                          ;; Wait for the delete's off-thread
                                          ;; search reindex before refreshing
                                          ;; either view - see
                                          ;; ui.completion.event.
                                          (rf/dispatch
                                           [::completion-event/await
                                            {:completion-id (:completion/id resp)
                                             :on-complete   [::refresh-listing
                                                             {:listing-id listing-id}]}])))
                                (p/catch (fn [err]
                                           (logging/error (str "Unable to delete selected " label) err)))))})
         {})
       {}))))

(rf/reg-event-fx
 ::export-grid
 (fn [_ [_ {:keys [listing-id include-associated-persons?]}]]
   {:fx [[:dispatch [::run-export-grid {:listing-id listing-id
                                        :include-associated-persons? include-associated-persons?}]]]}))

(rf/reg-event-fx
 ::run-export-grid
 (fn [{:keys [db]} [_ {:keys [listing-id include-associated-persons?]}]]
   (let [runtime (get-in db (generic-model/listing-path listing-id))
         entity-type (get-in runtime [:params :entity-type])
         view-mode (get-in runtime [:view :mode])
         grid-id (listing-grid-id listing-id)
         kanban-id (listing-kanban-id listing-id)
         grid-state (get-in db (grid-model/grid-path grid-id))
         kanban-compiled-fields (->> (get-in db (conj (kanban-model/compiled-config-path kanban-id) :fields) [])
                                     (keep normalize-field-path)
                                     vec)
         filter-model (get grid-state grid-model/filters-key)
         config (get grid-state grid-model/config-key)
         ;; The toolbar's query text and filters belong to the LISTING, not to
         ;; one view mode: the search box and filter bar live in the generic
         ;; listing and are handed down to whichever mode is rendered, which
         ;; then keeps a copy. Reading that copy out of grid state meant a
         ;; kanban export ignored them entirely - the grid component is never
         ;; mounted in kanban view, so all three read nil and the export ran
         ;; unfiltered - and a user who had been in grid view earlier got that
         ;; view's stale filters instead. Either way the spreadsheet did not
         ;; match what was on screen, with nothing to indicate it.
         ;;
         ;; `filter-model` above stays grid-scoped: it is the ag-grid column
         ;; filter, which genuinely only exists in grid view.
         filters-editor-id (or (get-in runtime [:filters :editor-id])
                               (str listing-id "-filters"))
         external-filters-by-field-path (get-in db (conj (simple-filters-model/filters-path filters-editor-id)
                                                         :by-field-path))
         external-filter-context (get-in db (generic-model/context-path listing-id))
         external-query-text (get-in db (conj (generic-model/query-path listing-id) :text))
         {:keys [columns hidden-columns]} config
         grid-visible-columns (->> columns
                                   (remove (set hidden-columns))
                                   vec)
         card-selected-fields (->> (get-in runtime [:view-config :card :by-view-mode view-mode :body-fields])
                                   (keep normalize-field-path)
                                   vec)
         default-card-fields (->> (or (:body-fields (listing-definition/card-config entity-type view-mode)) [])
                                  (keep normalize-field-path)
                                  vec)
         effective-card-fields (if (seq card-selected-fields)
                                 card-selected-fields
                                 default-card-fields)
         export-fields (if (= view-mode :kanban)
                         (cond
                           (seq kanban-compiled-fields) kanban-compiled-fields
                           (seq effective-card-fields) effective-card-fields
                           ;; Same last resort as generic/bulk-actions - see the
                           ;; comment there. It matters more here: this listing
                           ;; declares :initial-view-mode :kanban, so an empty
                           ;; resolution breaks export in the mode users land
                           ;; in, not one they have to switch to.
                           :else (listing-definition/po-default-field-paths entity-type))
                         grid-visible-columns)
         query-context (assoc (or external-filter-context {})
                              :entity-type
                              entity-type
                              :query-field-paths
                              export-fields)
         request-body {:entity-type entity-type
                       :fields export-fields
                       :opensearch-queries (concat []
                                                   (listing-definition/toolbar-filter-clauses
                                                    external-filters-by-field-path
                                                    external-filter-context)
                                                   (listing-definition/toolbar-query-clauses
                                                    external-query-text
                                                    query-context)
                                                   (when filter-model
                                                     (grid-event/filter-model->os-clauses filter-model columns)))
                       :pipeline-id (get-in runtime [:params :pipeline-id])
                       :include-associated-persons? (boolean include-associated-persons?)}]
     (if (seq export-fields)
       {:fx [[:dispatch [::export-grid-started]]
             [::ajax/send {:operation :search/export-grid
                           :body request-body
                           :on-ok [::export-grid-succeeded {:grid-id grid-id}]
                           :on-error [::export-grid-failed]}]]}
       {:fx [[:dispatch [::export-grid-failed {:message "No columns selected for export."}]]]}))))

(rf/reg-event-fx
 ::export-grid-started
 (fn [_ _]
   {::toast/info {:label "Preparing Excel export..."}}))

(rf/reg-event-fx
 ::export-grid-succeeded
 (fn [_ [_ {:keys [grid-id]} resp]]
   ;; The endpoint omits :attachment-id and reports :row-count 0 when nothing
   ;; matched, rather than uploading an empty spreadsheet. It is the only side
   ;; that can tell: it has run the query, whereas every piece of listing state
   ;; the client could consult clears to empty rather than to zero when a
   ;; filter matches nothing. See ai/mistakes.md (2026-09-08).
   (let [attachment-id (get-in resp [:body :attachment-id])]
     (if attachment-id
       (do
         (fu/open-attachment! attachment-id)
         {:fx [[:dispatch [::grid-event/trigger-query-search grid-id]]]
          ::toast/success (loc/tr :export-ready-download-started)})
       {::toast/info {:label (loc/tr :export-nothing-to-export)}}))))

(defn- export-error-message
  [err]
  (let [friendly-message (get-in err [:client-friendly-error-data :message])
        fallback-message (:message err)]
    (cond
      (and (string? friendly-message) (seq friendly-message)) friendly-message
      (and (string? fallback-message) (seq fallback-message)) fallback-message
      :else "Please try again.")))

(rf/reg-event-fx
 ::export-grid-failed
 (fn [_ [_ err]]
   (let [error-body (or (:response err) err)]
     (logging/error "Unable to export pipeline entity grid" err)
     {::toast/error (str "Excel export failed: " (export-error-message error-body))})))

(rf/reg-event-fx
 ::create-entity
 (fn [_ [_ {:keys [entity-type pipeline-attribute pipeline-id pipeline selected-pipeline]}]]
   (let [pipeline-id (or pipeline-id
                         (some-> selected-pipeline deref))
         flow-fn (get entity/opportunity-flow-fns entity-type)
         stage-items (when (and flow-fn
                                (flow-fn)
                                pipeline)
                       (->> (create-dropdown/pipeline-stage-items {:entity-type entity-type
                                                                   :pipeline pipeline})
                            ;; pipeline-stage-items always prepends a
                            ;; non-actionable {:section-label? true ...}
                            ;; header (no :on-click) for its own dropdown
                            ;; rendering - drop it here so (first ...) below
                            ;; lands on a real, clickable stage item.
                            (remove :section-label?)))]
     (if-let [first-stage-action (first stage-items)]
       ((:on-click first-stage-action) nil)
       (entity/open-creator! entity-type
                             (cond-> {}
                               (some? pipeline-id)
                               (assoc pipeline-attribute pipeline-id)))))
   {}))

(defn- stage-create-items
  [entity-type pipeline]
  (->> (create-dropdown/pipeline-stage-items {:entity-type entity-type
                                              :pipeline pipeline})
       (mapv (fn [{:keys [key label on-click items section-label?]}]
               (cond-> {:id key
                        :label label
                        :on-click on-click}
                 ;; `pipeline-stage-items` prepends a non-actionable
                 ;; {:section-label? true} header. The dropdown renders that as
                 ;; an uppercase label, but only if the flag survives - and
                 ;; re-mapping the items here used to drop it, so "Select stage"
                 ;; rendered as though it were a selectable stage.
                 section-label?
                 (assoc :section-label? true)

                 (seq items)
                 (assoc :items items))))))

(defn- create-action
  [{:keys [create-action-id create-label entity-type pipeline pipeline-attribute pipeline-id]}]
  (let [flow-fn (get entity/opportunity-flow-fns entity-type)
        stage-items (when (and flow-fn
                               (flow-fn)
                               pipeline)
                      (stage-create-items entity-type pipeline))]
    (cond-> {:id       create-action-id
             :label    (loc/tr create-label)
             :visible? (not (session/is-read-only-user?))
             :enabled? true
             :event    [::create-entity
                        {:entity-type entity-type
                         :pipeline-attribute pipeline-attribute
                         :pipeline-id pipeline-id
                         :pipeline pipeline}]}
      (seq stage-items)
      (assoc :items stage-items))))

(defn- admin-actions
  [{:keys [pipeline entity-type stage-entity-type stage-name-attribute stage-pipeline-attribute]}]
  (when (session/is-admin?)
    [{:id       :pipeline-stages
      :label    (loc/tr :pipeline-stages)
      :visible? true
      :enabled? true
      :on-click (fn [_]
                  (modals/open-modal!
                   pipeline-settings-curtain/modal-key
                   {:pipeline-id          (:db/id pipeline)
                    :stage-entity-type    stage-entity-type
                    :stage-name-attribute stage-name-attribute
                    :pipeline-attribute   stage-pipeline-attribute}))}
     {:id       :pipeline-folders
      :label    (loc/tr :pipeline-folders)
      :visible? true
      :enabled? true
      :on-click (fn [_]
                  (modals/open-modal!
                   folder-management-modal/modal-key
                   {:entity pipeline}))}
     (when-not (session/is-read-only-user?)
       {:id       :pipeline-checklists
        :label    (loc/tr :pipeline-checklists)
        :visible? true
        :enabled? true
        :on-click (fn [_]
                    (modals/open-modal!
                     folder-management-check-lists-modal/modal-key
                     {:entity           pipeline
                      :check-lists-atom session/check-lists}))})
     (grid-utils/creator-config-toolbar-action {:entity-type entity-type
                                                :pipeline    pipeline})]))

(defn- export-actions
  [listing-id entity-type]
  (let [all-teams (->> @session/all-teams
                       (remove :common/disabled?))
        account-teams (teams/resolve-account-teams @session/account all-teams)
        export-access? (import-permissions/export-access? @session/tenant account-teams @session/role)
        visible? (and export-access?
                      (import-permissions/allow-export? @session/tenant @session/role)
                      (not (session/is-mobile?)))]
    [{:id       :export-grid
      :label    (loc/tr :action/export)
      :visible? visible?
      :enabled? export-access?
      :event    [::export-grid
                 {:listing-id listing-id
                  :entity-type entity-type
                  :include-associated-persons? false}]}
     {:id       :export-grid-with-persons
      :label    (loc/tr :action/export-with-persons)
      :visible? visible?
      :enabled? export-access?
      :event    [::export-grid
                 {:listing-id listing-id
                  :entity-type entity-type
                  :include-associated-persons? true}]}]))

(defn- toolbar-actions
  [{:keys [runtime-id entity-type route-id pipeline-id pipeline-key] :as config}]
  (->> (concat [(create-action (assoc config :listing-id runtime-id))]
               (when-not (session/is-read-only-user?)
                 [{:id                  :bulk-edit
                   :label               (loc/tr :bulk-edit)
                   :visible?            true
                   :enabled?            true
                   :requires-selection? true
                   :event               [::open-bulk-edit {:listing-id runtime-id}]}
                  {:id                  :bulk-delete
                   :label               (loc/tr :confirm-delete-selected-button)
                   :visible?            true
                   :enabled?            true
                   :requires-selection? true
                   :event               [::bulk-delete-selected
                                         {:listing-id runtime-id
                                          :entity-type entity-type}]}])
               (export-actions runtime-id entity-type)
               (admin-actions config)
               [(grid-utils/preferred-landing-page-toggle {:view-id route-id
                                                           :pipeline-id pipeline-id
                                                           :pipeline-key pipeline-key
                                                           :inset? false})])
       (remove nil?)
       vec))

(defn- view-overrides
  "Extra props for the view of each view mode. The Kanban of each pipeline entity
   type has a menu of status actions on its cards. `on-open-won-selector!` opens
   the selector of the sale agreement of a sales opportunity."
  [{:keys [entity-type on-open-won-selector!]}]
  (when (card-actions/supported? entity-type)
    {:kanban {:card-actions-fn         (card-actions/card-actions-fn
                                        {:entity-type           entity-type
                                         :on-open-won-selector! on-open-won-selector!})
              :card-status-fn (card-style/status-fn entity-type)}}))

(defn page-content-inner
  [{:keys [listing-id
           entity-type
           pipeline-id
           pipeline
           route-id
           pipeline-attribute
           title]
    :as config}]
  (let [runtime-id (runtime-listing-id listing-id pipeline-id)]
    (r/with-let [_ (rf/dispatch
                    [::generic-event/init
                     {:listing-id           runtime-id
                      :initial-view-mode    :kanban
                      :entity-type          entity-type
                      :pipeline-id          pipeline-id
                      :type-listing-id      listing-id
                      :default-views        (default-views/default-views
                                             {:entity-type entity-type
                                              :account     @session/account
                                              :now-millis  (js/Date.now)
                                              :tr          loc/tr})
                      :filters              {:enabled? true
                                             :view-id route-id
                                             :load-facets-event [::generic-facets-event/load-facets]}
                      :available-view-modes #{:kanban :grid}
                      :pipeline             pipeline
                      :toolbar-actions      (toolbar-actions
                                             (assoc config
                                                    :runtime-id runtime-id
                                                    :pipeline-id pipeline-id
                                                    :pipeline pipeline))}])
                 _ (rf/dispatch
                    [::completion-event/listen
                     {:listener-id   runtime-id
                      :kind          completion/search-index-kind
                      :on-completion [::on-index-completion {:listing-id runtime-id}]}])
                 won-selector* (r/atom nil)]
      (let [bulk-edit-open? @(rf/subscribe [::bulk-edit-open? runtime-id])
            selected-ids @(rf/subscribe [::generic-sub/selected-ids runtime-id])
            context @(rf/subscribe [::generic-sub/context runtime-id])
            initialized? @(rf/subscribe [::generic-sub/initialized? runtime-id])
            accessible-field-paths (->> (or (:accessible-fields context) [])
                                        (keep :field-path)
                                        vec)
            bulk-edit-ready? (and initialized?
                                  (seq accessible-field-paths))
            setup-state (r/atom {:selected             (->> selected-ids
                                                            (map (fn [id] [id true]))
                                                            (into {}))
                                 :sorted-grid-entities (->> selected-ids
                                                            (mapv (fn [id]
                                                                    {:db/id id})))})
            setup-args {:state       setup-state
                        :column-keys (fn [] accessible-field-paths)
                        :mandatory?  (fn [_] false)}]
        [:<>
         [listing/view
          {:listing-id               runtime-id
           :title                    (pipeline-title title pipeline)
           :title-menu               (title-menu (assoc config :pipeline-id pipeline-id))
           :show-view-mode-selector? true
           :query-placeholder        (loc/tr :search)
           :extra-view-props         {:pipeline-id pipeline-id}
           :on-create-first-item     (fn []
                                       (rf/dispatch [::create-entity
                                                     {:entity-type entity-type
                                                      :pipeline-attribute pipeline-attribute
                                                      :pipeline-id pipeline-id
                                                      :pipeline pipeline}]))
           :on-row-clicked           (partial open-entity! entity-type)
           :on-card-clicked          (partial open-entity! entity-type)
           :view-overrides           (view-overrides
                                      {:entity-type           entity-type
                                       :on-open-won-selector! (fn [sales-opportunity]
                                                                (reset! won-selector* sales-opportunity))})}]
         (when (and bulk-edit-open?
                    bulk-edit-ready?)
           [bulk-edit-modal/view
            {:setup-args      setup-args
             :entity-type     entity-type
             :on-save-success #(rf/dispatch [::kanban-event/reload-visible-columns
                                             {:kanban-id (listing-kanban-id runtime-id)}])
             :close-modal!    #(rf/dispatch [::close-bulk-edit
                                             {:listing-id runtime-id}])}])
         (when-let [sales-opportunity @won-selector*]
           [sales-won-selector/view
            {:sales-opportunity sales-opportunity
             :on-close!         #(reset! won-selector* nil)}])
         [creator-editor/modal-view]])
      (finally
        (rf/dispatch [::completion-event/unlisten runtime-id])
        (rf/dispatch [::generic-event/destroy runtime-id])))))

(defn page-content
  [{:keys [listing-id selected-pipeline all-pipelines] :as config}]
  (let [pipeline-id @selected-pipeline
        pipelines @all-pipelines
        pipeline (selected-pipeline-entity pipelines pipeline-id)]
    (if (and (some? pipeline-id)
             (some? pipeline))
      ^{:key (runtime-listing-id listing-id pipeline-id)}
      [page-content-inner (assoc config
                                 :pipeline-id pipeline-id
                                 :pipeline pipeline)]
      [:div {:class "tw2 w-full h-full flex items-center justify-center text-sm text-base-light-text-paler"}
       "Loading listing..."])))

(defn render-page
  [{:keys [feature?
           all-pipelines]
    :as config}]
  (let [forbidden? (or (not (feature?))
                       (and (not (session/is-admin?))
                            (->> @all-pipelines
                                 (filter session/has-entity-access?)
                                 (empty?))))
        no-pipeline-in-use? (and (feature?)
                                 (session/is-admin?)
                                 (->> @all-pipelines
                                      (every? :pipeline/inactive?)))]
    (cond
      forbidden?
      [not-found/not-found-view]

      no-pipeline-in-use?
      [grid-components/no-pipelines-in-use-view]

      :else
      [default-page-layout/view
       [page-content config]])))
